import { customElement, observable, resolve, type IDisposable, IEventAggregator } from 'aurelia';
import { route } from '@aurelia/router';
import { I18N, Signals } from '@aurelia/i18n';
import template from './gallery-view.html';
import { addMediaItemsToCollection,
bulkUpdateCapturedAt, completeImportProtocol, createImportProtocol, deleteMediaItem, foldMediaItems, formatFileSize, getChronologyTree, getMediaPreviewUrl, listMediaItems, transferMediaOwnership, updateMediaItem, uploadMediaFile } from '../services/api.service';
import { getDefaultVisibility } from '../services/default-visibility.service';
import { getShowFamilyMedia } from '../services/media-scope.service';
import { hasPermission, PERMISSIONS_CHANGED_CHANNEL } from '../services/permissions.service';
import { CollectionFilter, MediaType, MediaTypeFilter, PersonReviewFilter, type AdvancedPersonFilter, type ChronologyTree, type Collection, type CollectionRef, type MediaFoldGroup, type MediaItem, type MediaVisibility, type Person, type Tag, type TagRef } from '../services/api.types';
import type { AppUser } from '../services/api.types';
import type { FoldChangeOperation } from '../components/media-lightbox';
import type { UploadProgressItem } from '../components/media-upload-progress';
import { ConfirmDialog } from '../components/confirm-dialog';

const CHRONOLOGY_STORAGE_KEY = 'radish_media_show_chronology';
const FILTERBAR_STORAGE_KEY = 'radish_media_show_filterbar';
const FILTER_MODE_STORAGE_KEY = 'radish_media_gallery_filter_mode';
const TAGS_OVERLAY_STORAGE_KEY = 'radish_media_show_tags_overlay';
const TIME_OVERLAY_STORAGE_KEY = 'radish_media_show_time_overlay';
const FILENAME_OVERLAY_STORAGE_KEY = 'radish_media_show_filename_overlay';
const FILTERS_STORAGE_KEY = 'radish_media_gallery_filters';
const SEARCH_DEBOUNCE_MS = 400;
const SELECT_ALL_CONFIRM_THRESHOLD = 100;
const SELECT_ALL_PAGE_SIZE = 100;
const TOUCH_TAP_MAX_DELAY_MS = 450;
const TOUCH_TAP_MAX_MOVEMENT_PX = 18;

// Maximum number of tag chips shown directly on a gallery tile before collapsing
// the rest into a "+N" indicator (hover reveals the remaining tag names).
const MAX_VISIBLE_TILE_TAGS = 3;

interface GalleryScrollAnchor {
  itemId: string | null;
  fallbackIndex: number;
  offsetTop: number;
  scrollTop: number;
  focusedItemId: string | null;
  hadTileFocus: boolean;
}

export type ChronologySelectionType = 'all' | 'unknown' | 'year' | 'month' | 'day';
type BreadcrumbType = ChronologySelectionType | 'dateFilter';
type FilterDropdown = 'personMatch' | 'searchScope' | 'tagMatch' | null;
type PersonFilterMode = 'any' | 'all' | 'advanced';
type SelectionAction = 'collection' | 'family' | 'private' | 'fold' | 'transfer' | 'date' | 'chronology';

interface GalleryFilterState {
  searchText: string;
  includeDescription: boolean;
  filterTags: Tag[];
  matchAllTags: boolean;
  dateFrom: string;
  dateTo: string;
  mediaTypeFilter: MediaTypeFilter;
  protocolNumberFilter: string;
  personReviewFilter: PersonReviewFilter;
  filterPersons: Person[];
  matchAllPersons: boolean;
  personFilterMode: PersonFilterMode;
  advancedPersonFilter: AdvancedPersonFilter | null;
  collectionFilter?: CollectionFilter;
  onlyWithoutCollection?: boolean;
  onlyFolded?: boolean;
  embeddedMetadataFilter?: boolean | null;
  captureTimeAccuracyFilter?: boolean | null;
  sortOption: string;
  selType: ChronologySelectionType;
  selYear: number;
  selMonth: number;
  selDay: number;
}

interface ChronologySelectionState {
  selType: ChronologySelectionType;
  selYear: number;
  selMonth: number;
  selDay: number;
}

function loadSavedFilterState(): Partial<GalleryFilterState> {
  try {
    const raw = localStorage.getItem(FILTERS_STORAGE_KEY);
    return raw ? JSON.parse(raw) as Partial<GalleryFilterState> : {};
  } catch {
    return {};
  }
}

function loadSavedCollectionFilter(saved: Partial<GalleryFilterState>): CollectionFilter {
  if (saved.collectionFilter === CollectionFilter.InCollection
    || saved.collectionFilter === CollectionFilter.NotInCollection) {
    return saved.collectionFilter;
  }
  return saved.onlyWithoutCollection ? CollectionFilter.NotInCollection : CollectionFilter.Any;
}

// Pre-expand the chronology tree branch leading to a restored year/month/day selection,
// so the selected node is visible (not hidden inside a collapsed year/month) as soon as
// the tree data loads.
function buildInitialExpandedYears(saved: Partial<GalleryFilterState>): Record<number, boolean> {
  const { selType, selYear } = saved;
  if ((selType === 'year' || selType === 'month' || selType === 'day') && selYear != null) {
    return { [selYear]: true };
  }
  return {};
}

function buildInitialExpandedMonths(saved: Partial<GalleryFilterState>): Record<string, boolean> {
  const { selType, selYear, selMonth } = saved;
  if ((selType === 'month' || selType === 'day') && selYear != null && selMonth != null) {
    return { [`${selYear}-${selMonth}`]: true };
  }
  return {};
}

function loadSavedAdvancedPersonFilter(saved: Partial<GalleryFilterState>): AdvancedPersonFilter | null {
  const filter = saved.advancedPersonFilter;
  if (!filter || !Array.isArray(filter.conditions) || filter.conditions.length === 0) return null;
  const conditions = filter.conditions.filter(condition =>
    !!condition
    && typeof condition.personId === 'string'
    && !!condition.person
    && typeof condition.person.id === 'string');
  const normalizedConditions = conditions.map(condition => {
    const faceTypes = Array.isArray(condition.faceTypes)
      ? [...condition.faceTypes]
      : condition.faceType == null ? [] : [condition.faceType];
    const faceSize = condition.faceSize ?? condition.minimumSize ?? null;
    const generalRating = condition.generalRating ?? condition.minimumGeneralRating ?? null;
    return {
      ...condition,
      faceTypes,
      faceType: undefined,
      faceSize,
      faceSizeComparison: condition.faceSizeComparison ?? (faceSize == null ? null : 'atLeast'),
      generalRating,
      generalRatingComparison: condition.generalRatingComparison
        ?? (generalRating == null ? null : 'atLeast'),
      minimumSize: undefined,
      minimumGeneralRating: undefined,
    };
  });
  return normalizedConditions.length > 0
    ? {
      operator: filter.operator === 'or' ? 'or' : 'and',
      conditions: normalizedConditions,
    }
    : null;
}

function loadSavedPersonFilterMode(
  saved: Partial<GalleryFilterState>,
  advancedPersonFilter: AdvancedPersonFilter | null,
): PersonFilterMode {
  if (saved.personFilterMode === 'any'
    || saved.personFilterMode === 'all'
    || saved.personFilterMode === 'advanced') {
    return saved.personFilterMode;
  }
  return advancedPersonFilter ? 'advanced' : (saved.matchAllPersons ? 'all' : 'any');
}

function createAdvancedPersonFilter(
  persons: Person[],
  matchAllPersons: boolean,
): AdvancedPersonFilter | null {
  if (persons.length === 0) return null;
  return {
    operator: matchAllPersons ? 'and' : 'or',
    conditions: persons.map(person => ({
      personId: person.id,
      person,
      negated: false,
      faceTypes: null,
      faceType: null,
      faceSize: null,
      faceSizeComparison: null,
      generalRating: null,
      generalRatingComparison: null,
    })),
  };
}

@route({ path: ['', 'gallery'], title: 'Gallery' })
@customElement({ name: 'gallery-view', template })
export class GalleryView {
  private readonly i18n: I18N = resolve(I18N);
  private readonly ea: IEventAggregator = resolve(IEventAggregator);
  private _localeSub: IDisposable | null = null;
  private _permissionsSub: IDisposable | null = null;

  // Tracked so dirty-checking re-evaluates monthName() and breadcrumbItems when locale changes
  public _locale = '';

  // Snapshot of previously saved filter/sort/chronology-selection state (read once per instance)
  private readonly _savedFilters = loadSavedFilterState();

  // Guards to avoid auto-refresh firing during initial construction/binding or while batch-clearing filters
  private _ready = false;
  private _suppressAutoFilter = false;
  private _searchDebounceHandle: ReturnType<typeof setTimeout> | null = null;

  public mediaItems: MediaItem[] = [];
  public totalCount = 0;
  public page = 1;
  public pageSize = 24;
  @observable public sortOption = this._savedFilters.sortOption ?? 'capturedat:desc';
  @observable public searchText = this._savedFilters.searchText ?? '';
  @observable public includeDescription = !!this._savedFilters.searchText?.trim()
    && (this._savedFilters.includeDescription ?? false);
  @observable public filterTags: Tag[] = this._savedFilters.filterTags ?? [];
  @observable public matchAllTags = this._savedFilters.matchAllTags ?? true;
  @observable public dateFrom = this._savedFilters.dateFrom ?? '';
  @observable public dateTo = this._savedFilters.dateTo ?? '';
  @observable public mediaTypeFilter = this._savedFilters.mediaTypeFilter ?? MediaTypeFilter.All;
  @observable public protocolNumberFilter = this._savedFilters.protocolNumberFilter ?? '';
  @observable public personReviewFilter = this._savedFilters.personReviewFilter ?? PersonReviewFilter.None;
  @observable public embeddedMetadataFilter = typeof this._savedFilters.embeddedMetadataFilter === 'boolean'
    ? this._savedFilters.embeddedMetadataFilter
    : null;
  @observable public captureTimeAccuracyFilter = typeof this._savedFilters.captureTimeAccuracyFilter === 'boolean'
    ? this._savedFilters.captureTimeAccuracyFilter
    : null;
  @observable public filterPersons: Person[] = this._savedFilters.filterPersons ?? [];
  @observable public matchAllPersons = this._savedFilters.matchAllPersons ?? false;
  @observable public advancedPersonFilter = loadSavedAdvancedPersonFilter(this._savedFilters);
  @observable public personFilterMode = loadSavedPersonFilterMode(
    this._savedFilters,
    this.advancedPersonFilter,
  );
  @observable public collectionFilter = loadSavedCollectionFilter(this._savedFilters);
  @observable public onlyFolded = this._savedFilters.onlyFolded ?? false;
  public isLoading = false;
  public isRefreshing = false;
  public isLoadingMore = false;
  public isLoadingMoreLightbox = false;
  public error = '';
  public dragOverlayVisible = false;
  public uploadItems: UploadProgressItem[] = [];
  public uploadModalVisible = false;
  public uploadStartedAt: number | null = null;
  public lightboxItems: MediaItem[] = [];
  public lightboxStartIndex = 0;
  private _touchTapStart: { index: number; x: number; y: number } | null = null;
  private _lastTouchTap: { index: number; at: number } | null = null;
  private _touchDoubleTapHandledAt = 0;
  private _penTapHandledAt = 0;
  private _mediaRequestVersion = 0;

  private dragDepth = 0;

  // ── Selection (multi-delete) ─────────────────────────────────────────────────
  public selectMode = false;
  public selectedIds: Record<string, boolean> = {};
  private _lastSelectedIdx = -1;
  public rangeSelectionMode = false;
  public isSelectingAll = false;
  public selectAllProgress = 0;
  public selectAllTotal = 0;
  public showFoldModal = false;
  public foldItems: MediaItem[] = [];
  public folding = false;
  public showBulkDateModal = false;
  public bulkDateUpdating = false;
  public showChronologyOrderModal = false;
  public chronologyOrderItems: MediaItem[] = [];
  public chronologyOrderUpdating = false;
  public showOwnerPicker = false;
  public hasMaintenanceAccess = hasPermission('maintenance.access');
  public transferring = false;

  public get hasSelection(): boolean { return this.selectionCount > 0; }
  public get selectionCount(): number { return Object.values(this.selectedIds).filter(Boolean).length; }
  public get canSelectRange(): boolean {
    return this.hasSelection && this._lastSelectedIdx >= 0 && this._lastSelectedIdx < this.mediaItems.length;
  }
  public get selectedMediaItems(): MediaItem[] {
    return this.mediaItems.filter(item => this.selectedIds[item.id]);
  }
  public get canFoldSelection(): boolean {
    return this.selectionCount >= 2
      && this.selectedMediaItems.every(item => item.mediaType === MediaType.Image);
  }
  public get canSetSelectedDate(): boolean {
    return this.hasSelection
      && this.selectedMediaItems.every(item => !item.capturedAt || !item.isCapturedAtTimeAccurate);
  }
  public get canOrderSelectionChronologically(): boolean {
    const items = this.selectedMediaItems;
    if (items.length < 2) return false;
    const firstDate = items[0].capturedAt ? this.localDateKey(new Date(items[0].capturedAt)) : null;
    return firstDate !== null
      && items.every(item =>
        !!item.capturedAt
        && !item.isCapturedAtTimeAccurate
        && this.localDateKey(new Date(item.capturedAt)) === firstDate);
  }
  public get canTransferSelection(): boolean {
    return this.hasMaintenanceAccess && this.hasSelection;
  }

  // ── Keyboard navigation (roving tabindex) ────────────────────────────────────
  public focusedIndex = 0;
  public galleryGridEl: HTMLElement | null = null;

  // ── Chronology tree state (grouped by captured date) ────────────────────────
  // Read directly from localStorage as field initializers so the value is correct
  // by the time the router's `loading()` hook runs (which fires before `binding()`).
  public showChronology = localStorage.getItem(CHRONOLOGY_STORAGE_KEY) === 'true';
  public chronologyTree: ChronologyTree | null = null;
  public expandedYears: Record<number, boolean> = buildInitialExpandedYears(this._savedFilters);
  public expandedMonths: Record<string, boolean> = buildInitialExpandedMonths(this._savedFilters);
  public selType: ChronologySelectionType = this._savedFilters.selType ?? 'all';
  public selYear = this._savedFilters.selYear ?? 0;
  public selMonth = this._savedFilters.selMonth ?? 0;
  public selDay = this._savedFilters.selDay ?? 0;
  private _chronologySelectionBeforeDateFilter: ChronologySelectionState | null = null;

  public get dateFilterActive(): boolean {
    return !!this.dateFrom || !!this.dateTo;
  }

  public get dateInputLanguage(): 'de' | 'en-GB' {
    return this._locale.toLowerCase().startsWith('de') ? 'de' : 'en-GB';
  }

  public get chronologyVisible(): boolean {
    return this.showChronology && !this.dateFilterActive;
  }

  // ── Filter bar visibility ─────────────────────────────────────────────────
  public showFilterBar = localStorage.getItem(FILTERBAR_STORAGE_KEY) !== 'false';
  public showAdvancedFilters = localStorage.getItem(FILTER_MODE_STORAGE_KEY) === 'advanced';
  public filterDropdown: FilterDropdown = null;
  public filterDropdownHost: HTMLElement | null = null;
  public showAdvancedPersonFilterModal = false;
  public selectionActionsOpen = false;
  public selectionActionsHost: HTMLElement | null = null;
  private _pendingAdvancedModeSwitch: {
    mode: 'any' | 'all';
    persons: Person[];
    advancedFilter: AdvancedPersonFilter | null;
  } | null = null;
  private readonly _filterDropdownDocumentHandler = (event: MouseEvent): void => {
    const target = event.target;
    if (target instanceof Node && !this.selectionActionsHost?.contains(target)) {
      this.selectionActionsOpen = false;
    }
    if (target instanceof Node && this.filterDropdownHost?.contains(target)) return;
    this.filterDropdown = null;
  };

  // ── Tag overlay visibility (persisted across view/route changes) ────────────
  public showTags = localStorage.getItem(TAGS_OVERLAY_STORAGE_KEY) === 'true';

  // ── Captured-time overlay visibility (persisted across view/route changes) ──
  public showTime = localStorage.getItem(TIME_OVERLAY_STORAGE_KEY) === 'true';

  // ── Filename visibility (persisted across view/route changes) ──────────────
  public showFileName = localStorage.getItem(FILENAME_OVERLAY_STORAGE_KEY) === 'true';

  // ── Infinite scroll ────────────────────────────────────────────────────────
  public scrollContainer: HTMLElement | null = null;
  public scrollSentinel: HTMLElement | null = null;
  public visibleMediaStart = 0;
  public visibleMediaEnd = 0;

  private _scrollObserver: IntersectionObserver | null = null;
  private _visibleRangeResizeObserver: ResizeObserver | null = null;
  private _visibleRangeFrame: number | null = null;
  private readonly _visibleRangeScrollHandler = (): void => this.scheduleVisibleMediaRangeUpdate();
  private readonly _visibleRangeResizeHandler = (): void => this.scheduleVisibleMediaRangeUpdate();

  public get hasMore(): boolean {
    return this.mediaItems.length < this.totalCount;
  }

  // The lightbox keeps its own snapshot (`lightboxItems`) that can grow independently of the
  // grid's `mediaItems` — e.g. `onLightboxPersonDataChanged()` resets `mediaItems`/`totalCount`
  // every time a person gets marked (to keep the grid/tree in sync with the shrinking filtered
  // set), while marked items are NOT removed from `lightboxItems` (the user is still browsing
  // them). Comparing `lightboxItems.length` against the ever-shrinking `totalCount` would then
  // go "no more" prematurely — e.g. mark item 5 of 20 total, totalCount drops to 19, but only 5
  // items are loaded into the lightbox and 15 more still exist on the server. So track whether
  // the lightbox has actually exhausted the server-side result set explicitly instead.
  public lightboxExhausted = false;

  public get lightboxHasMore(): boolean {
    return !this.lightboxExhausted;
  }

  public binding(): void {
    this._locale = this.i18n.getLocale();
    this._localeSub = this.ea.subscribe(Signals.I18N_EA_CHANNEL, () => {
      this._locale = this.i18n.getLocale();
      // Deep-clone the tree so repeat.for sees new object references and
      // re-evaluates monthName() calls inside the nested repeaters.
      if (this.chronologyTree) {
        this.chronologyTree = JSON.parse(JSON.stringify(this.chronologyTree)) as ChronologyTree;
      }
    });
    this._permissionsSub = this.ea.subscribe(PERMISSIONS_CHANGED_CHANNEL, () => {
      this.hasMaintenanceAccess = hasPermission('maintenance.access');
    });
  }

  public async loading(): Promise<void> {
    const params = new URLSearchParams(window.location.search);
    const protocolNumber = params.get('protocolNumber');
    if (protocolNumber && /^\d+$/.test(protocolNumber)) {
      this.protocolNumberFilter = protocolNumber;
    }
    // "Jump to gallery filtered by this person" link from the persons list — the id/name
    // fields come straight from the query string so no extra lookup call is needed here.
    const personId = params.get('personId');
    if (personId) {
      this.filterPersons = [{
        id: personId,
        firstName: params.get('personFirstName') ?? '',
        lastName: params.get('personLastName') ?? '',
        nickName: params.get('personNickName') ?? '',
      }];
      this.matchAllPersons = false;
      this.personFilterMode = 'any';
      this.advancedPersonFilter = null;
    }
    this.updateChronologyForDateFilter();
    await this.fetchMediaItems();
    // Always (re-)fetch the chronology tree when the view loads/reloads so it never
    // shows stale data or gets stuck on "loading…" while the tree panel is visible.
    if (this.chronologyVisible) void this.reloadChronologyTree();
    // Enable auto-refresh-on-change only after the initial load completes, so restoring
    // saved filter state above doesn't trigger duplicate fetches.
    this._ready = true;
  }

  public attached(): void {
    document.addEventListener('click', this._filterDropdownDocumentHandler);
    this.scrollContainer?.addEventListener('scroll', this._visibleRangeScrollHandler, { passive: true });
    window.addEventListener('resize', this._visibleRangeResizeHandler);
    if (this.scrollContainer) {
      this._visibleRangeResizeObserver = new ResizeObserver(() => this.scheduleVisibleMediaRangeUpdate());
      this._visibleRangeResizeObserver.observe(this.scrollContainer);
    }
    if (this.scrollSentinel && this.scrollContainer) {
      this._scrollObserver = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) void this._loadMore();
        },
        { root: this.scrollContainer, threshold: 0.1 },
      );
      this._scrollObserver.observe(this.scrollSentinel);
    }
    this.scheduleVisibleMediaRangeUpdate();
  }

  public detaching(): void {
    document.removeEventListener('click', this._filterDropdownDocumentHandler);
    this.scrollContainer?.removeEventListener('scroll', this._visibleRangeScrollHandler);
    window.removeEventListener('resize', this._visibleRangeResizeHandler);
    this._visibleRangeResizeObserver?.disconnect();
    this._visibleRangeResizeObserver = null;
    this._scrollObserver?.disconnect();
    this._scrollObserver = null;
    if (this._visibleRangeFrame !== null) {
      cancelAnimationFrame(this._visibleRangeFrame);
      this._visibleRangeFrame = null;
    }
    this._localeSub?.dispose();
    this._permissionsSub?.dispose();
    if (this._searchDebounceHandle) clearTimeout(this._searchDebounceHandle);
  }

  public get hasFilters(): boolean {
    return !!this.searchText
      || this.includeDescription
      || this.filterTags.length > 0
      || !!this.dateFrom
      || !!this.dateTo
      || !!this.protocolNumberFilter.trim()
      || this.mediaTypeFilter !== MediaTypeFilter.All
      || this.personReviewFilter !== PersonReviewFilter.None
      || this.filterPersons.length > 0
      || (this.advancedPersonFilter?.conditions.length ?? 0) > 0
      || this.collectionFilter !== CollectionFilter.Any
      || this.onlyFolded
      || this.embeddedMetadataFilter !== null
      || this.captureTimeAccuracyFilter !== null
      || this.selType !== 'all';
  }

  public async fetchMediaItems(): Promise<void> {
    const requestVersion = ++this._mediaRequestVersion;
    const activeElement = document.activeElement;
    const hadTileFocus = !!this.galleryGridEl
      && activeElement instanceof HTMLElement
      && this.galleryGridEl.contains(activeElement)
      && activeElement.classList.contains('gallery-tile');
    this._saveFilterState();
    this.page = 1;
    this.isRefreshing = false;
    this.isLoading = true;
    this.isLoadingMore = false;
    this.error = '';
    this.clearSelection();
    this.focusedIndex = 0;
    this.tileHasFocus = false;
    try {
      const result = await this._fetchFirstPage(this.pageSize);
      if (requestVersion !== this._mediaRequestVersion) return;
      this.mediaItems = result.mediaItems;
      this.totalCount = result.totalCount;
    } catch (error) {
      if (requestVersion === this._mediaRequestVersion) {
        this.error = error instanceof Error ? error.message : String(error);
      }
    } finally {
      if (requestVersion !== this._mediaRequestVersion) return;
      this.isLoading = false;
      this.scheduleVisibleMediaRangeUpdate();
      if (hadTileFocus && this.lightboxItems.length === 0 && this.mediaItems.length > 0) {
        requestAnimationFrame(() => {
          void this.focusTile(Math.min(this.focusedIndex, this.mediaItems.length - 1));
        });
      }
    }
  }

  private captureGalleryScrollAnchor(): GalleryScrollAnchor {
    const fallbackIndex = this.mediaItems.length > 0
      ? Math.min(this.focusedIndex, this.mediaItems.length - 1)
      : 0;
    const focusedItemId = this.tileHasFocus
      ? this.mediaItems[this.focusedIndex]?.id ?? null
      : null;
    const container = this.scrollContainer;
    const tiles = this.getTileElements();
    if (!container || tiles.length === 0) {
      return {
        itemId: this.mediaItems[fallbackIndex]?.id ?? null,
        fallbackIndex,
        offsetTop: 0,
        scrollTop: container?.scrollTop ?? 0,
        focusedItemId,
        hadTileFocus: this.tileHasFocus,
      };
    }

    const containerRect = container.getBoundingClientRect();
    const firstVisibleIndex = tiles.findIndex(tile => {
      const rect = tile.getBoundingClientRect();
      return rect.bottom > containerRect.top && rect.top < containerRect.bottom;
    });
    const anchorIndex = firstVisibleIndex >= 0 ? firstVisibleIndex : fallbackIndex;
    const anchorTile = tiles[anchorIndex];
    const anchorRect = anchorTile?.getBoundingClientRect();

    return {
      itemId: this.mediaItems[anchorIndex]?.id ?? null,
      fallbackIndex: anchorIndex,
      offsetTop: anchorRect ? anchorRect.top - containerRect.top : 0,
      scrollTop: container.scrollTop,
      focusedItemId,
      hadTileFocus: this.tileHasFocus,
    };
  }

  private async restoreGalleryScrollAnchor(anchor: GalleryScrollAnchor): Promise<void> {
    const container = this.scrollContainer;
    if (!container) return;

    await new Promise<void>(resolve => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
    if (this.mediaItems.length === 0) {
      container.scrollTop = 0;
      this.visibleMediaStart = 0;
      this.visibleMediaEnd = 0;
      return;
    }

    const tiles = this.getTileElements();
    let targetIndex = anchor.itemId
      ? this.mediaItems.findIndex(item => item.id === anchor.itemId)
      : -1;
    if (targetIndex < 0) {
      targetIndex = Math.min(Math.max(anchor.fallbackIndex, 0), this.mediaItems.length - 1);
    }

    const target = tiles[targetIndex];
    const focusedIndex = anchor.focusedItemId
      ? this.mediaItems.findIndex(item => item.id === anchor.focusedItemId)
      : -1;
    const focusTargetIndex = focusedIndex >= 0 ? focusedIndex : targetIndex;
    const focusTarget = tiles[focusTargetIndex];
    this.focusedIndex = focusTargetIndex;
    if (anchor.hadTileFocus && focusTarget) {
      focusTarget.focus({ preventScroll: true });
      this.tileHasFocus = true;
    }

    if (target) {
      const containerRect = container.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      container.scrollTop += targetRect.top - containerRect.top - anchor.offsetTop;
    } else {
      container.scrollTop = anchor.scrollTop;
    }
    this.scheduleVisibleMediaRangeUpdate();
  }

  private async fetchRefreshWindow(
    previousItems: MediaItem[],
    changedIds?: Iterable<string>,
  ): ReturnType<typeof listMediaItems> {
    const changedIdSet = changedIds ? new Set(changedIds) : null;
    if (changedIdSet && previousItems.length > 0) {
      const firstChangedIndex = previousItems.findIndex(item => changedIdSet.has(item.id));
      if (firstChangedIndex > 0) {
        const prefix = previousItems.slice(0, firstChangedIndex);
        const result = await this._fetchAfter(
          previousItems[firstChangedIndex - 1].id,
          Math.max(1, previousItems.length - firstChangedIndex),
        );
        return {
          mediaItems: [...prefix, ...result.mediaItems],
          totalCount: result.totalCount,
        };
      }
    }

    return this._fetchFirstPage(Math.max(this.pageSize, previousItems.length));
  }

  private applyRefreshedMediaItems(
    refreshedItems: MediaItem[],
    changedIds?: Iterable<string>,
  ): void {
    const previousItems = this.mediaItems;
    const changedIdSet = changedIds ? new Set(changedIds) : null;
    const firstChangedIdIndex = changedIdSet
      ? previousItems.findIndex(item => changedIdSet.has(item.id))
      : -1;
    const firstDifferentIndex = previousItems.findIndex(
      (item, index) => refreshedItems[index]?.id !== item.id,
    );
    const firstChangedIndex = firstChangedIdIndex >= 0
      ? firstDifferentIndex >= 0
        ? Math.min(firstChangedIdIndex, firstDifferentIndex)
        : firstChangedIdIndex
      : firstDifferentIndex;

    if (firstChangedIndex < 0) {
      this.mediaItems = refreshedItems;
      return;
    }

    this.mediaItems = [
      ...previousItems.slice(0, firstChangedIndex),
      ...refreshedItems.slice(firstChangedIndex),
    ];
  }

  private async refreshMediaItemsPreservingPosition(changedIds?: Iterable<string>): Promise<void> {
    if (this.isLoading || this.isSelectingAll) return;

    const requestVersion = ++this._mediaRequestVersion;
    const anchor = this.captureGalleryScrollAnchor();
    const previousItems = [...this.mediaItems];
    this.page = 1;
    this.isRefreshing = true;
    this.error = '';
    this.clearSelection();

    try {
      const result = await this.fetchRefreshWindow(previousItems, changedIds);
      if (requestVersion !== this._mediaRequestVersion) return;
      this.applyRefreshedMediaItems(result.mediaItems, changedIds);
      this.totalCount = result.totalCount;
      await this.restoreGalleryScrollAnchor(anchor);
    } catch (error) {
      if (requestVersion === this._mediaRequestVersion) {
        this.error = error instanceof Error ? error.message : String(error);
      }
    } finally {
      if (requestVersion !== this._mediaRequestVersion) return;
      this.isRefreshing = false;
      this.scheduleVisibleMediaRangeUpdate();
    }
  }

  private async _loadMore(): Promise<void> {
    if (this.isLoading || this.isRefreshing || this.isLoadingMore || this.isSelectingAll || !this.hasMore) return;
    // Keyset pagination: cursor off the last currently-loaded item's id (per the active sort)
    // rather than an offset/page number. Offset paging would silently skip or repeat items
    // whenever the filtered set shrinks/grows between requests — e.g. person-review filters as
    // photos get marked while paging through the lightbox — since "page 2" always means
    // "items 25-48 of the CURRENT result set", not "the next 24 unseen items".
    const afterId = this.mediaItems.at(-1)?.id;
    if (!afterId) return;
    const requestVersion = this._mediaRequestVersion;
    this.isLoadingMore = true;
    try {
      const result = await this._fetchAfter(afterId);
      if (requestVersion !== this._mediaRequestVersion) return;
      // De-dupe defensively in case the cursor item itself got re-included (e.g. edge case
      // where it no longer matches the current filter but was already in mediaItems).
      const existingIds = new Set(this.mediaItems.map(item => item.id));
      this.mediaItems = [...this.mediaItems, ...result.mediaItems.filter(item => !existingIds.has(item.id))];
      this.totalCount = result.totalCount;
    } finally {
      this.isLoadingMore = false;
      this.scheduleVisibleMediaRangeUpdate();
    }
  }

  // Loads the next page for the lightbox's own item list, cursoring off the lightbox's own
  // last-loaded item — NOT the grid's `mediaItems`, which can be reset independently (see
  // `lightboxHasMore` above). Kept separate from `_loadMore()` so marking people while paging
  // through the lightbox never causes it to skip/duplicate items relative to its own position.
  private async _loadMoreForLightbox(): Promise<void> {
    if (this.isLoading || this.isLoadingMoreLightbox || !this.lightboxHasMore) return;
    const afterId = this.lightboxItems.at(-1)?.id;
    if (!afterId) return;
    this.isLoadingMoreLightbox = true;
    try {
      const result = await this._fetchAfter(afterId);
      const existingIds = new Set(this.lightboxItems.map(item => item.id));
      const newItems = result.mediaItems.filter(item => !existingIds.has(item.id));
      this.lightboxItems = [...this.lightboxItems, ...newItems];
      this.totalCount = result.totalCount;
      // The server returning fewer rows than requested (including zero) is the only
      // reliable "no more" signal here — `totalCount` shrinks independently as items get
      // marked (see `lightboxExhausted` doc comment above), so it can't be used for this.
      if (result.mediaItems.length < this.pageSize) this.lightboxExhausted = true;
    } finally {
      this.isLoadingMoreLightbox = false;
    }
  }

  private _fetchFirstPage(pageSize: number): ReturnType<typeof listMediaItems> {
    const [sortBy, direction] = this.sortOption.split(':');
    const { dateFrom, dateTo, onlyUnknownCapturedAt } = this.effectiveDateRange();
    return listMediaItems({
      sortBy,
      sortDescending: direction !== 'asc',
      page: 1,
      pageSize,
      dateFrom,
      dateTo,
      onlyUnknownCapturedAt,
      searchText: this.searchText.trim() || undefined,
      includeDescription: this.includeDescription,
      tagIds: this.filterTags.filter(tag => tag.id).map(tag => tag.id),
      matchAllTags: this.matchAllTags,
      mediaType: this.mediaTypeFilter,
      protocolNumber: this.parsedProtocolNumber,
      personReviewFilter: this.personReviewFilter,
      personIds: this.personFilterMode === 'advanced' ? [] : this.filterPersons.map(p => p.id),
      matchAllPersons: this.personFilterMode === 'advanced' ? false : this.matchAllPersons,
      advancedPersonFilter: this.personFilterMode === 'advanced'
        ? this.advancedPersonFilter ?? undefined
        : undefined,
      onlyWithoutCollection: this.collectionFilter === CollectionFilter.NotInCollection,
      onlyWithCollection: this.collectionFilter === CollectionFilter.InCollection,
      onlyFolded: this.onlyFolded,
      hasEmbeddedMetadata: this.embeddedMetadataFilter ?? undefined,
      isCapturedAtTimeAccurate: this.captureTimeAccuracyFilter ?? undefined,
      showFamilyMedia: getShowFamilyMedia(),
    });
  }

  private _fetchAfter(afterId: string, pageSize = this.pageSize): ReturnType<typeof listMediaItems> {
    const [sortBy, direction] = this.sortOption.split(':');
    const { dateFrom, dateTo, onlyUnknownCapturedAt } = this.effectiveDateRange();
    return listMediaItems({
      sortBy,
      sortDescending: direction !== 'asc',
      pageSize,
      dateFrom,
      dateTo,
      onlyUnknownCapturedAt,
      searchText: this.searchText.trim() || undefined,
      includeDescription: this.includeDescription,
      tagIds: this.filterTags.filter(tag => tag.id).map(tag => tag.id),
      matchAllTags: this.matchAllTags,
      mediaType: this.mediaTypeFilter,
      protocolNumber: this.parsedProtocolNumber,
      personReviewFilter: this.personReviewFilter,
      personIds: this.personFilterMode === 'advanced' ? [] : this.filterPersons.map(p => p.id),
      matchAllPersons: this.personFilterMode === 'advanced' ? false : this.matchAllPersons,
      advancedPersonFilter: this.personFilterMode === 'advanced'
        ? this.advancedPersonFilter ?? undefined
        : undefined,
      onlyWithoutCollection: this.collectionFilter === CollectionFilter.NotInCollection,
      onlyWithCollection: this.collectionFilter === CollectionFilter.InCollection,
      onlyFolded: this.onlyFolded,
      hasEmbeddedMetadata: this.embeddedMetadataFilter ?? undefined,
      isCapturedAtTimeAccurate: this.captureTimeAccuracyFilter ?? undefined,
      showFamilyMedia: getShowFamilyMedia(),
      afterId,
    });
  }

  public async applyFilters(): Promise<void> {
    await this.fetchMediaItems();
    if (this.chronologyVisible) void this.reloadChronologyTree();
    else if (this.dateFilterActive) this.chronologyTree = null;
  }

  public clearFilters(): void {
    // Suppress the per-field auto-refresh while batch-resetting, then refresh once at the end.
    this._suppressAutoFilter = true;
    this.searchText = '';
    this.includeDescription = false;
    this.filterTags = [];
    this.matchAllTags = true;
    this.dateFrom = '';
    this.dateTo = '';
    this.protocolNumberFilter = '';
    this.mediaTypeFilter = MediaTypeFilter.All;
    this.personReviewFilter = PersonReviewFilter.None;
    this.filterPersons = [];
    this.matchAllPersons = false;
    this.personFilterMode = 'any';
    this.advancedPersonFilter = null;
    this.collectionFilter = CollectionFilter.Any;
    this.onlyFolded = false;
    this.embeddedMetadataFilter = null;
    this.captureTimeAccuracyFilter = null;
    this.selType = 'all';
    this._chronologySelectionBeforeDateFilter = null;
    this._suppressAutoFilter = false;
    void this.applyFilters();
  }

  public toggleFilterBar(): void {
    this.showFilterBar = !this.showFilterBar;
    localStorage.setItem(FILTERBAR_STORAGE_KEY, String(this.showFilterBar));
  }

  public togglePersonMatchDropdown(): void {
    this.filterDropdown = this.filterDropdown === 'personMatch' ? null : 'personMatch';
  }

  public personMatchIcon(): string {
    if (this.personFilterMode === 'advanced') return 'fa-sliders';
    return this.personFilterMode === 'all' ? 'fa-users' : 'fa-user-group';
  }

  public personMatchTitleKey(): string {
    if (this.personFilterMode === 'advanced') return 'gallery.filters.advancedPersonSearch';
    return this.personFilterMode === 'all'
      ? 'gallery.filters.matchAll'
      : 'gallery.filters.matchAny';
  }

  public get advancedPersonConditionCount(): number {
    return this.advancedPersonFilter?.conditions.length ?? 0;
  }

  public setPersonFilterMode(mode: PersonFilterMode): void {
    if (this.isSelectingAll) return;
    this.filterDropdown = null;
    if (mode === this.personFilterMode) {
      if (mode === 'advanced') this.openAdvancedPersonFilter();
      return;
    }

    if (mode === 'advanced') {
      this._pendingAdvancedModeSwitch = {
        mode: this.personFilterMode === 'all' ? 'all' : 'any',
        persons: [...this.filterPersons],
        advancedFilter: this.advancedPersonFilter,
      };
      this._suppressAutoFilter = true;
      this.personFilterMode = 'advanced';
      if (!this.advancedPersonFilter) {
        this.advancedPersonFilter = createAdvancedPersonFilter(
          this.filterPersons,
          this.matchAllPersons,
        );
      }
      this._suppressAutoFilter = false;
      this.showAdvancedPersonFilterModal = true;
      return;
    }

    this._suppressAutoFilter = true;
    if (this.personFilterMode === 'advanced') {
      const restoredPersons = this.advancedPersonFilter?.conditions
        .map(condition => condition.person)
        .filter((person, index, persons): person is Person =>
          !!person && persons.findIndex(candidate => candidate?.id === person.id) === index)
        ?? [];
      this.filterPersons = restoredPersons;
    }
    this.advancedPersonFilter = null;
    this.personFilterMode = mode;
    this.matchAllPersons = mode === 'all';
    this._pendingAdvancedModeSwitch = null;
    this._suppressAutoFilter = false;
    if (!this._ready) return;
    void this.applyFilters();
  }

  public toggleTagMatchDropdown(): void {
    this.filterDropdown = this.filterDropdown === 'tagMatch' ? null : 'tagMatch';
  }

  public setTagMatch(matchAll: boolean): void {
    this.matchAllTags = matchAll;
    this.filterDropdown = null;
  }

  public toggleSearchScopeDropdown(): void {
    this.filterDropdown = this.filterDropdown === 'searchScope' ? null : 'searchScope';
  }

  public setSearchScope(includeDescription: boolean): void {
    this.includeDescription = includeDescription;
    this.filterDropdown = null;
  }

  public clearSearchFilter(): void {
    this.searchText = '';
  }

  public clearTagFilter(): void {
    this.filterTags = [];
  }

  public clearPersonFilter(): void {
    if (this.personFilterMode === 'advanced') {
      this.applyAdvancedPersonFilter(null);
      return;
    }

    this.filterPersons = [];
  }

  public openAdvancedPersonFilter(): void {
    if (this.isSelectingAll) return;
    if (this.personFilterMode !== 'advanced') {
      this.setPersonFilterMode('advanced');
      return;
    }
    if (!this.advancedPersonFilter && this.filterPersons.length > 0) {
      this.advancedPersonFilter = createAdvancedPersonFilter(
        this.filterPersons,
        this.matchAllPersons,
      );
    }
    this.showAdvancedPersonFilterModal = true;
  }

  public closeAdvancedPersonFilter(): void {
    if (this._pendingAdvancedModeSwitch) {
      const pending = this._pendingAdvancedModeSwitch;
      this._suppressAutoFilter = true;
      this.personFilterMode = pending.mode;
      this.matchAllPersons = pending.mode === 'all';
      this.filterPersons = pending.persons;
      this.advancedPersonFilter = pending.advancedFilter;
      this._suppressAutoFilter = false;
      this._pendingAdvancedModeSwitch = null;
    }
    this.showAdvancedPersonFilterModal = false;
  }

  public applyAdvancedPersonFilter(filter: AdvancedPersonFilter | null): void {
    this._suppressAutoFilter = true;
    this.advancedPersonFilter = filter;
    this.personFilterMode = filter ? 'advanced' : 'any';
    this.filterPersons = [];
    this.matchAllPersons = false;
    this._pendingAdvancedModeSwitch = null;
    this.showAdvancedPersonFilterModal = false;
    this._suppressAutoFilter = false;
    if (!this._ready || this._suppressAutoFilter) return;
    void this.applyFilters();
  }

  public clearProtocolNumberFilter(): void {
    this.protocolNumberFilter = '';
  }

  public onProtocolNumberInput(event: Event): void {
    const input = event.currentTarget;
    if (!(input instanceof HTMLInputElement)) return;
    const numericValue = input.value.replace(/\D/g, '');
    if (input.value !== numericValue) input.value = numericValue;
    this.protocolNumberFilter = numericValue;
  }

  public toggleAdvancedFilters(): void {
    this.showAdvancedFilters = !this.showAdvancedFilters;
    localStorage.setItem(FILTER_MODE_STORAGE_KEY, this.showAdvancedFilters ? 'advanced' : 'simple');
  }

  public toggleTags(): void {
    this.showTags = !this.showTags;
    localStorage.setItem(TAGS_OVERLAY_STORAGE_KEY, String(this.showTags));
  }

  public toggleTime(): void {
    this.showTime = !this.showTime;
    localStorage.setItem(TIME_OVERLAY_STORAGE_KEY, String(this.showTime));
  }

  public toggleFileName(): void {
    this.showFileName = !this.showFileName;
    localStorage.setItem(FILENAME_OVERLAY_STORAGE_KEY, String(this.showFileName));
  }

  // Time-of-day portion of a media item's captured timestamp, shown in the tile overlay.
  public tileTime(item: MediaItem): string {
    if (!item.capturedAt) return '';
    const d = new Date(item.capturedAt);
    if (isNaN(d.getTime())) return '';
    return new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' }).format(d);
  }

  // Tags on a MediaItem already arrive ordered by priority (tag group position,
  // then tag position, then name) from the backend — see MediaGrpcService.MapToResponse.
  public visibleTileTags(item: MediaItem): TagRef[] {
    return item.tags.slice(0, MAX_VISIBLE_TILE_TAGS);
  }

  public hiddenTileTags(item: MediaItem): TagRef[] {
    return item.tags.slice(MAX_VISIBLE_TILE_TAGS);
  }

  public hiddenTileTagsCount(item: MediaItem): number {
    return Math.max(0, item.tags.length - MAX_VISIBLE_TILE_TAGS);
  }

  // Native title tooltip is used instead of a custom popover so the full list is
  // never clipped by the tile's own overflow or the gallery scroll container.
  public hiddenTileTagsTitle(item: MediaItem): string {
    return this.hiddenTileTags(item).map(tag => tag.name).join(', ');
  }

  private _saveFilterState(): void {
    const chronologySelection = this.dateFilterActive && this._chronologySelectionBeforeDateFilter
      ? this._chronologySelectionBeforeDateFilter
      : {
        selType: this.selType,
        selYear: this.selYear,
        selMonth: this.selMonth,
        selDay: this.selDay,
      };
    const state: GalleryFilterState = {
      searchText: this.searchText,
      includeDescription: this.includeDescription,
      filterTags: this.filterTags,
      matchAllTags: this.matchAllTags,
      dateFrom: this.dateFrom,
      dateTo: this.dateTo,
      mediaTypeFilter: this.mediaTypeFilter,
      protocolNumberFilter: this.protocolNumberFilter,
      personReviewFilter: this.personReviewFilter,
      filterPersons: this.personFilterMode === 'advanced' ? [] : this.filterPersons,
      matchAllPersons: this.personFilterMode === 'advanced' ? false : this.matchAllPersons,
      personFilterMode: this.personFilterMode,
      advancedPersonFilter: this.advancedPersonFilter,
      collectionFilter: this.collectionFilter,
      onlyFolded: this.onlyFolded,
      embeddedMetadataFilter: this.embeddedMetadataFilter,
      captureTimeAccuracyFilter: this.captureTimeAccuracyFilter,
      sortOption: this.sortOption,
      selType: chronologySelection.selType,
      selYear: chronologySelection.selYear,
      selMonth: chronologySelection.selMonth,
      selDay: chronologySelection.selDay,
    };
    try {
      localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(state));
    } catch { /* ignore quota/serialization errors */ }
  }

  // ── Auto-refresh on filter/sort change (no "Apply" button) ──────────────────
  // Text search is debounced so it doesn't refetch on every keystroke; the rest
  // of the filters refresh immediately since they change less frequently.

  public searchTextChanged(): void {
    if (!this._ready || this._suppressAutoFilter) return;
    if (!this.searchText.trim()) {
      this.includeDescription = false;
      this.filterDropdown = null;
    }
    if (this._searchDebounceHandle) clearTimeout(this._searchDebounceHandle);
    this._searchDebounceHandle = setTimeout(() => {
      this._searchDebounceHandle = null;
      void this.applyFilters();
    }, SEARCH_DEBOUNCE_MS);
  }

  public includeDescriptionChanged(): void {
    if (!this._ready || this._suppressAutoFilter || !this.searchText.trim()) return;
    void this.applyFilters();
  }

  public filterTagsChanged(): void {
    if (this.filterTags.length <= 1 && this.filterDropdown === 'tagMatch') this.filterDropdown = null;
    if (!this._ready || this._suppressAutoFilter) return;
    void this.applyFilters();
  }

  public filterPersonsChanged(): void {
    if (this.filterPersons.length <= 1) this.filterDropdown = null;
    if (!this._ready || this._suppressAutoFilter) return;
    void this.applyFilters();
  }

  public matchAllPersonsChanged(): void {
    if (!this._ready || this._suppressAutoFilter) return;
    void this.applyFilters();
  }

  public advancedPersonFilterChanged(): void {
    if (!this._ready || this._suppressAutoFilter) return;
    void this.applyFilters();
  }

  public matchAllTagsChanged(): void {
    if (!this._ready || this._suppressAutoFilter) return;
    void this.applyFilters();
  }

  public dateFromChanged(): void {
    this.updateChronologyForDateFilter();
    if (!this._ready || this._suppressAutoFilter) return;
    void this.applyFilters();
  }

  public dateToChanged(): void {
    this.updateChronologyForDateFilter();
    if (!this._ready || this._suppressAutoFilter) return;
    void this.applyFilters();
  }

  public mediaTypeFilterChanged(): void {
    if (!this._ready || this._suppressAutoFilter) return;
    void this.applyFilters();
  }

  public personReviewFilterChanged(): void {
    if (!this._ready || this._suppressAutoFilter) return;
    void this.applyFilters();
  }

  public collectionFilterChanged(): void {
    if (!this._ready || this._suppressAutoFilter) return;
    void this.applyFilters();
  }

  public onlyFoldedChanged(): void {
    if (!this._ready || this._suppressAutoFilter) return;
    void this.applyFilters();
  }

  public embeddedMetadataFilterChanged(): void {
    if (!this._ready || this._suppressAutoFilter) return;
    void this.applyFilters();
  }

  public captureTimeAccuracyFilterChanged(): void {
    if (!this._ready || this._suppressAutoFilter) return;
    void this.applyFilters();
  }

  public protocolNumberFilterChanged(): void {
    if (!this._ready || this._suppressAutoFilter) return;
    void this.applyFilters();
  }

  public get parsedProtocolNumber(): number | undefined {
    const value = this.protocolNumberFilter.trim();
    if (!value || !/^\d+$/.test(value)) return undefined;
    const parsed = Number.parseInt(value, 10);
    return parsed > 0 ? parsed : undefined;
  }

  public sortOptionChanged(): void {
    if (!this._ready || this._suppressAutoFilter) return;
    void this.applyFilters();
  }

  public previewUrl(item: MediaItem): string {
    return getMediaPreviewUrl(item.id, item.lastUpdatedAt || item.uploadedAt);
  }

  public isVideo(item: MediaItem): boolean {
    return item.mediaType === MediaType.Video;
  }

  public fileSize(item: MediaItem): string {
    return formatFileSize(item.fileSizeBytes);
  }

  public openLightbox(index: number): void {
    this.lightboxItems = [...this.mediaItems];
    this.lightboxStartIndex = index;
    // hasMore mirrors the grid's exhaustion state at the moment the lightbox opens (they
    // share the same items array at this point); it then evolves independently while paging.
    this.lightboxExhausted = !this.hasMore;
  }

  // Invoked by the lightbox when the user navigates past the last currently-loaded
  // item and `hasMore` is true — reuses the same infinite-scroll pagination as the
  // grid, then refreshes the (snapshot-copied) items array the lightbox is bound to.
  public async requestMoreForLightbox(): Promise<void> {
    await this._loadMoreForLightbox();
  }

  // ── Selection (multi-delete) ─────────────────────────────────────────────────

  public toggleSelectMode(): void {
    if (this.isSelectingAll) return;
    this.selectMode = !this.selectMode;
    this.selectionActionsOpen = false;
    if (!this.selectMode) this.clearSelection();
  }

  public clearSelection(): void {
    this.selectedIds = {};
    this._lastSelectedIdx = -1;
    this.rangeSelectionMode = false;
    this.selectionActionsOpen = false;
  }

  public toggleSelectionActions(event: Event): void {
    event.stopPropagation();
    if (!this.hasSelection || this.isSelectingAll) return;
    this.selectionActionsOpen = !this.selectionActionsOpen;
  }

  public onSelectionActionsKeyDown(event: KeyboardEvent): void {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.selectionActionsOpen = false;
    this.selectionActionsHost?.querySelector<HTMLButtonElement>('.selection-bar__icon-button')?.focus();
  }

  public runSelectionAction(action: SelectionAction): void {
    if (!this.hasSelection || this.isSelectingAll) return;
    this.selectionActionsOpen = false;
    switch (action) {
      case 'collection':
        this.openCollectionPicker();
        break;
      case 'family':
        void this.setSelectedVisibility('family');
        break;
      case 'private':
        void this.setSelectedVisibility('private');
        break;
      case 'fold':
        this.openFoldModal();
        break;
      case 'transfer':
        this.openOwnerPicker();
        break;
      case 'date':
        this.openBulkDateModal();
        break;
      case 'chronology':
        this.openChronologyOrderModal();
        break;
    }
  }

  public openBulkDateModal(): void {
    if (!this.canSetSelectedDate || this.bulkDateUpdating || this.isSelectingAll) return;
    this.showBulkDateModal = true;
  }

  public closeBulkDateModal(): void {
    if (!this.bulkDateUpdating) this.showBulkDateModal = false;
  }

  public openChronologyOrderModal(): void {
    if (!this.canOrderSelectionChronologically || this.chronologyOrderUpdating || this.isSelectingAll) return;
    this.chronologyOrderItems = [...this.selectedMediaItems];
    this.showChronologyOrderModal = true;
  }

  public closeChronologyOrderModal(): void {
    if (this.chronologyOrderUpdating) return;
    this.showChronologyOrderModal = false;
    this.chronologyOrderItems = [];
  }

  public async confirmBulkDate(localDate: string): Promise<void> {
    if (this.bulkDateUpdating || !this.canSetSelectedDate) return;

    const items = [...this.selectedMediaItems];
    try {
      const undatedItems = items.filter(item => !item.capturedAt);
      const updates = items.map(item => ({
        mediaItemId: item.id,
        capturedAt: this.buildCapturedAtForDate(
          item,
          localDate,
          item.capturedAt ? 0 : undatedItems.indexOf(item),
          item.capturedAt ? 0 : undatedItems.length,
        ),
      }));

      this.bulkDateUpdating = true;
      await bulkUpdateCapturedAt({
        updates,
        mode: 'date',
        localDate,
        localUtcOffsetMinutes: -new Date(`${localDate}T12:00:00`).getTimezoneOffset(),
      });
      this.showBulkDateModal = false;
      this.selectMode = false;
      this.clearSelection();
      await this.refreshMediaItemsPreservingPosition(items.map(item => item.id));
      if (this.chronologyVisible) void this.reloadChronologyTree();
    } catch (error) {
      await ConfirmDialog.alert(
        error instanceof Error ? error.message : String(error),
        this.i18n.tr('gallery.bulkDate.failed'),
      );
    } finally {
      this.bulkDateUpdating = false;
    }
  }

  public async confirmChronologyOrder(items: MediaItem[]): Promise<void> {
    if (this.chronologyOrderUpdating || !this.canOrderSelectionChronologically) return;

    const orderedItems = [...items];
    const localDate = orderedItems[0]?.capturedAt
      ? this.localDateKey(new Date(orderedItems[0].capturedAt))
      : null;
    if (!localDate || orderedItems.length < 2) return;

    try {
      const updates = orderedItems.map((item, index) => ({
        mediaItemId: item.id,
        capturedAt: this.buildCapturedAtForDate(item, localDate, index, orderedItems.length, true),
      }));

      this.chronologyOrderUpdating = true;
      await bulkUpdateCapturedAt({
        updates,
        mode: 'chronological',
        localDate,
        localUtcOffsetMinutes: -new Date(`${localDate}T12:00:00`).getTimezoneOffset(),
      });
      this.showChronologyOrderModal = false;
      this.chronologyOrderItems = [];
      this.selectMode = false;
      this.clearSelection();
      await this.refreshMediaItemsPreservingPosition(orderedItems.map(item => item.id));
      if (this.chronologyVisible) void this.reloadChronologyTree();
    } catch (error) {
      await ConfirmDialog.alert(
        error instanceof Error ? error.message : String(error),
        this.i18n.tr('gallery.chronologyOrder.failed'),
      );
    } finally {
      this.chronologyOrderUpdating = false;
    }
  }

  private localDateKey(date: Date): string {
    if (Number.isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private buildCapturedAtForDate(
    item: MediaItem,
    localDate: string,
    sequenceIndex: number,
    sequenceCount: number,
    forceSequentialTime = false,
  ): string {
    const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(localDate);
    if (!parts) throw new Error(this.i18n.tr('gallery.bulkDate.invalidDate'));

    const year = Number(parts[1]);
    const month = Number(parts[2]) - 1;
    const day = Number(parts[3]);
    const existing = item.capturedAt ? new Date(item.capturedAt) : null;
    if (existing && Number.isNaN(existing.getTime()))
      throw new Error(this.i18n.tr('gallery.bulkDate.invalidDate'));

    const sortDescending = this.sortOption.endsWith(':desc');
    const seconds = forceSequentialTime || !existing
      ? (sortDescending ? sequenceCount - 1 - sequenceIndex : sequenceIndex)
      : 0;
    const preserveExistingTime = !!existing && !forceSequentialTime;
    const target = new Date(
      year,
      month,
      day,
      preserveExistingTime ? existing.getHours() : 0,
      preserveExistingTime ? existing.getMinutes() : 0,
      (preserveExistingTime ? existing.getSeconds() : 0) + seconds,
      preserveExistingTime ? existing.getMilliseconds() : 0,
    );
    return target.toISOString();
  }

  public openFoldModal(): void {
    if (!this.canFoldSelection) return;
    this.foldItems = [...this.selectedMediaItems];
    this.showFoldModal = true;
  }

  public closeFoldModal(): void {
    if (this.folding) return;
    this.showFoldModal = false;
    this.foldItems = [];
  }

  public async confirmFold(representativeId: string): Promise<void> {
    if (this.folding || this.foldItems.length < 2) return;
    this.folding = true;
    const changedIds = this.foldItems.map(item => item.id);
    try {
      await foldMediaItems(this.foldItems.map(item => item.id), representativeId);
      this.showFoldModal = false;
      this.foldItems = [];
      this.selectMode = false;
      await this.refreshMediaItemsPreservingPosition(changedIds);
      if (this.chronologyVisible) void this.reloadChronologyTree();
    } catch (error) {
      await ConfirmDialog.alert(
        error instanceof Error ? error.message : String(error),
        this.i18n.tr('gallery.folding.failed'),
      );
    } finally {
      this.folding = false;
    }
  }

  public openOwnerPicker(): void {
    if (!this.canTransferSelection || this.transferring || this.isSelectingAll) return;
    this.showOwnerPicker = true;
  }

  public closeOwnerPicker(): void {
    if (this.transferring) return;
    this.showOwnerPicker = false;
  }

  public userLabel(user: AppUser): string {
    const displayName = user.displayName?.trim();
    return displayName && displayName !== user.username
      ? `${displayName} (${user.username})`
      : user.username;
  }

  public async confirmOwnerTransfer(target: AppUser): Promise<void> {
    if (this.transferring || !this.canTransferSelection) return;

    const ids = Object.keys(this.selectedIds).filter(id => this.selectedIds[id]);
    if (ids.length === 0) return;

    this.showOwnerPicker = false;
    const targetLabel = this.userLabel(target);
    const message = ids.length === 1
      ? this.i18n.tr('gallery.selection.transferOwnerConfirmOne', { target: targetLabel })
      : this.i18n.tr('gallery.selection.transferOwnerConfirmMany', { count: ids.length, target: targetLabel });
    const confirmed = await ConfirmDialog.confirm(
      message,
      this.i18n.tr('gallery.selection.transferOwnerTitle'),
    );
    if (!confirmed) return;

    this.transferring = true;
    try {
      const result = await transferMediaOwnership(ids, target.id);
      this.selectMode = false;
      await this.refreshMediaItemsPreservingPosition(ids);
      if (this.chronologyVisible) void this.reloadChronologyTree();
      const successKey = result.transferredCount === 0
        ? 'gallery.selection.transferOwnerSuccessNone'
        : result.transferredCount === 1
        ? 'gallery.selection.transferOwnerSuccessOne'
        : 'gallery.selection.transferOwnerSuccessMany';
      await ConfirmDialog.alert(
        this.i18n.tr(successKey, { count: result.transferredCount, target: targetLabel }),
        this.i18n.tr('gallery.selection.transferOwnerTitle'),
      );
    } catch (error) {
      await ConfirmDialog.alert(
        error instanceof Error ? error.message : this.i18n.tr('gallery.selection.transferOwnerFailed'),
        this.i18n.tr('common.error'),
      );
    } finally {
      this.transferring = false;
    }
  }

  public async selectAllVisible(): Promise<void> {
    if (this.isSelectingAll || this.isLoading || this.isRefreshing || this.isLoadingMore) return;

    const totalToSelect = this.totalCount;
    if (totalToSelect > SELECT_ALL_CONFIRM_THRESHOLD) {
      const confirmed = await ConfirmDialog.confirm(
        this.i18n.tr('gallery.selection.selectAllConfirm', { count: totalToSelect }),
        this.i18n.tr('gallery.selection.selectAllTitle'),
      );
      if (!confirmed) return;
    }

    this.isSelectingAll = true;
    this.selectAllProgress = Math.min(this.mediaItems.length, totalToSelect);
    this.selectAllTotal = totalToSelect;

    try {
      while (this.mediaItems.length < this.totalCount) {
        const afterId = this.mediaItems.at(-1)?.id;
        const result = afterId
          ? await this._fetchAfter(afterId, SELECT_ALL_PAGE_SIZE)
          : await this._fetchFirstPage(SELECT_ALL_PAGE_SIZE);
        const existingIds = new Set(this.mediaItems.map(item => item.id));
        const newItems = result.mediaItems.filter(item => !existingIds.has(item.id));

        this.totalCount = result.totalCount;
        this.selectAllTotal = this.totalCount;
        if (newItems.length === 0) {
          if (this.mediaItems.length >= this.totalCount) break;
          throw new Error(this.i18n.tr('gallery.selection.selectAllFailed'));
        }

        this.mediaItems = [...this.mediaItems, ...newItems];
        this.selectAllProgress = Math.min(this.mediaItems.length, this.selectAllTotal);
      }

      const next: Record<string, boolean> = {};
      for (const item of this.mediaItems) next[item.id] = true;
      this.selectedIds = next;
      this.selectMode = true;
      this._lastSelectedIdx = -1;
      this.rangeSelectionMode = false;
      this.selectAllProgress = this.mediaItems.length;
      this.selectAllTotal = this.mediaItems.length;
    } catch (error) {
      await ConfirmDialog.alert(
        error instanceof Error ? error.message : String(error),
        this.i18n.tr('gallery.selection.selectAllTitle'),
      );
    } finally {
      this.isSelectingAll = false;
      this.scheduleVisibleMediaRangeUpdate();
    }
  }

  public onTileClick(item: MediaItem, index: number, event: MouseEvent): void {
    if (this.consumePenTapClick(event)) return;
    if (this.rangeSelectionMode && this._lastSelectedIdx >= 0) {
      event.preventDefault();
      this.selectItemRange(this._lastSelectedIdx, index);
      return;
    }
    if (event.shiftKey && this._lastSelectedIdx >= 0) {
      event.preventDefault();
      this.selectItemRange(this._lastSelectedIdx, index);
      return;
    }
    if (this.selectMode || event.ctrlKey || event.metaKey) {
      event.preventDefault();
      if (event.ctrlKey || event.metaKey) this.toggleItemSelected(item, index);
      else this.selectSingleItem(item, index);
      return;
    }
    // Plain click only focuses the tile (native button focus already handles this);
    // track it as the shift-click anchor for range selection.
    this._lastSelectedIdx = index;
    this.focusedIndex = index;
  }

  public onTileDoubleClick(index: number, event: MouseEvent): void {
    if (this._touchDoubleTapHandledAt > 0) {
      const elapsed = Date.now() - this._touchDoubleTapHandledAt;
      this._touchDoubleTapHandledAt = 0;
      if (elapsed <= TOUCH_TAP_MAX_DELAY_MS) {
        event.preventDefault();
        return;
      }
    }
    event.preventDefault();
    this.openLightbox(index);
  }

  public onTilePointerDown(index: number, event: PointerEvent): void {
    if (!this.isTouchLikePointer(event)) return;
    if (this.isSelectionCheckboxTarget(event.target)) {
      this._touchTapStart = null;
      this._lastTouchTap = null;
      return;
    }
    this._touchTapStart = { index, x: event.clientX, y: event.clientY };
  }

  public onTilePointerUp(index: number, event: PointerEvent): void {
    if (!this.isTouchLikePointer(event)) return;
    const start = this._touchTapStart;
    this._touchTapStart = null;
    if (!start || start.index !== index || this.isSelectionCheckboxTarget(event.target)) {
      this._lastTouchTap = null;
      return;
    }

    const moved = Math.hypot(event.clientX - start.x, event.clientY - start.y);
    if (moved > TOUCH_TAP_MAX_MOVEMENT_PX) {
      this._lastTouchTap = null;
      return;
    }

    if (event.pointerType === 'pen') {
      this._lastTouchTap = null;
      if (!this.selectMode) {
        const now = Date.now();
        this._penTapHandledAt = now;
        this._touchDoubleTapHandledAt = now;
        event.preventDefault();
        this.openLightbox(index);
      }
      return;
    }

    const now = Date.now();
    if (this._lastTouchTap
      && this._lastTouchTap.index === index
      && now - this._lastTouchTap.at <= TOUCH_TAP_MAX_DELAY_MS) {
      this._lastTouchTap = null;
      this._touchDoubleTapHandledAt = now;
      event.preventDefault();
      this.openLightbox(index);
      return;
    }
    this._lastTouchTap = { index, at: now };
  }

  public onTilePointerCancel(event: PointerEvent): void {
    if (!this.isTouchLikePointer(event)) return;
    this._touchTapStart = null;
    this._lastTouchTap = null;
  }

  private isTouchLikePointer(event: PointerEvent): boolean {
    return event.pointerType === 'touch' || event.pointerType === 'pen';
  }

  private consumePenTapClick(event: MouseEvent): boolean {
    if (this._penTapHandledAt <= 0) return false;
    const elapsed = Date.now() - this._penTapHandledAt;
    this._penTapHandledAt = 0;
    if (elapsed > TOUCH_TAP_MAX_DELAY_MS) return false;
    event.preventDefault();
    return true;
  }

  private isSelectionCheckboxTarget(target: EventTarget | null): boolean {
    return target instanceof Element && !!target.closest('.gallery-tile__checkbox');
  }

  public toggleTileCheckbox(item: MediaItem, index: number, event: MouseEvent): void {
    event.stopPropagation();
    if (this.rangeSelectionMode && this._lastSelectedIdx >= 0) {
      event.preventDefault();
      this.selectItemRange(this._lastSelectedIdx, index);
    } else if (event.shiftKey && this._lastSelectedIdx >= 0) {
      event.preventDefault();
      this.selectItemRange(this._lastSelectedIdx, index);
    } else {
      this.toggleItemSelected(item, index);
    }
  }

  public toggleRangeSelectionMode(): void {
    if (this.isSelectingAll || !this.canSelectRange) return;
    this.rangeSelectionMode = !this.rangeSelectionMode;
  }

  private selectItemRange(anchorIndex: number, targetIndex: number): void {
    const start = Math.min(anchorIndex, targetIndex);
    const end = Math.max(anchorIndex, targetIndex);
    const next: Record<string, boolean> = {};
    for (let i = start; i <= end; i++) next[this.mediaItems[i].id] = true;
    this.selectedIds = next;
    this.selectMode = true;
    this.focusedIndex = targetIndex;
    this.rangeSelectionMode = false;
  }

  private selectSingleItem(item: MediaItem, index: number): void {
    this.selectedIds = { [item.id]: true };
    this._lastSelectedIdx = index;
    this.selectMode = true;
    this.rangeSelectionMode = false;
  }

  private toggleItemSelected(item: MediaItem, index: number): void {
    const next = { ...this.selectedIds };
    if (next[item.id]) delete next[item.id]; else next[item.id] = true;
    this.selectedIds = next;
    this._lastSelectedIdx = index;
    this.selectMode = true;
  }

  // ── Keyboard navigation (roving tabindex) ────────────────────────────────────
  // `focusedIndex` tracks the roving-tabindex target (kept even when focus moves
  // outside the grid, e.g. to the filter bar, so tab-back returns to the right tile).
  // `tileHasFocus` tracks whether a tile currently *actually* has DOM focus, so the
  // visual focus ring only shows while it's true (for both mouse clicks and keyboard
  // navigation) instead of relying on the browser's `:focus-visible` heuristic, which
  // hides the outline for mouse-triggered focus.
  public tileHasFocus = false;

  public onTileFocus(index: number): void {
    this.focusedIndex = index;
    this.tileHasFocus = true;
  }

  public onTileBlur(): void {
    this.tileHasFocus = false;
  }

  public onGridKeyDown(event: KeyboardEvent): void {
    if (this.lightboxItems.length > 0) return;
    if (event.defaultPrevented) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    const tile = target.closest<HTMLElement>('.gallery-tile');
    if (!tile || !this.galleryGridEl?.contains(tile)) return;

    const index = this.getTileElements().indexOf(tile);
    if (index < 0) return;
    this.onTileKeyDown(event, index);
  }

  public onTileKeyDown(event: KeyboardEvent, index: number): void {
    if (this.lightboxItems.length > 0) return;
    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault();
        void this.focusTile(index + 1);
        break;
      case 'ArrowLeft':
        event.preventDefault();
        void this.focusTile(index - 1);
        break;
      case 'ArrowDown':
        event.preventDefault();
        void this.focusTile(index + this.getColumnsCount());
        break;
      case 'ArrowUp':
        event.preventDefault();
        void this.focusTile(index - this.getColumnsCount());
        break;
      case ' ':
      case 'Spacebar':
        event.preventDefault();
        this.toggleItemSelected(this.mediaItems[index], index);
        break;
      case 'Enter':
        event.preventDefault();
        this.openLightbox(index);
        break;
      default:
        break;
    }
  }

  private getTileElements(): HTMLElement[] {
    if (!this.galleryGridEl) return [];
    return Array.from(this.galleryGridEl.querySelectorAll<HTMLElement>('.gallery-tile'));
  }

  private scheduleVisibleMediaRangeUpdate(): void {
    if (this._visibleRangeFrame !== null) return;
    this._visibleRangeFrame = requestAnimationFrame(() => {
      this._visibleRangeFrame = null;
      this.updateVisibleMediaRange();
    });
  }

  private updateVisibleMediaRange(): void {
    const container = this.scrollContainer;
    const tiles = this.getTileElements();
    if (this.isLoading || !container || tiles.length === 0) {
      this.visibleMediaStart = 0;
      this.visibleMediaEnd = 0;
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const viewportTop = Math.max(containerRect.top, 0);
    const viewportBottom = Math.min(containerRect.bottom, window.innerHeight);
    let firstVisible = -1;
    let lastVisible = -1;

    for (let index = 0; index < tiles.length; index++) {
      const rect = tiles[index].getBoundingClientRect();
      if (rect.bottom > viewportTop && rect.top < viewportBottom) {
        if (firstVisible < 0) firstVisible = index;
        lastVisible = index;
      }
    }

    if (firstVisible < 0) {
      this.visibleMediaStart = 0;
      this.visibleMediaEnd = 0;
      return;
    }

    this.visibleMediaStart = firstVisible + 1;
    this.visibleMediaEnd = lastVisible + 1;
  }

  private getColumnsCount(): number {
    const tiles = this.getTileElements();
    if (tiles.length === 0) return 1;
    const firstTop = tiles[0].offsetTop;
    let count = 0;
    for (const tile of tiles) {
      if (tile.offsetTop !== firstTop) break;
      count++;
    }
    return Math.max(1, count);
  }

  private async focusTile(index: number): Promise<void> {
    let tiles = this.getTileElements();
    if (index >= tiles.length && this.hasMore && !this.isLoadingMore) {
      await this._loadMore();
      // Wait a frame so Aurelia has rendered the newly appended tiles before we measure/focus them.
      await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
      tiles = this.getTileElements();
    }
    if (tiles.length === 0) return;
    const clamped = Math.max(0, Math.min(index, tiles.length - 1));
    this.focusedIndex = clamped;
    const target = tiles[clamped];
    target?.focus();
    target?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  public async deleteSelected(): Promise<void> {
    if (!this.hasSelection) return;

    const count = this.selectionCount;
    const message = count === 1
      ? this.i18n.tr('gallery.selection.deleteConfirmOne')
      : this.i18n.tr('gallery.selection.deleteConfirmMany', { count });
    const confirmed = await ConfirmDialog.confirm(message, this.i18n.tr('gallery.selection.deleteTitle'));
    if (!confirmed) return;

    try {
      const ids = Object.keys(this.selectedIds).filter(id => this.selectedIds[id]);
      for (const id of ids) {
        await deleteMediaItem(id);
      }
      this.clearSelection();
      this.selectMode = false;
      // Deleting a representative detaches its folded children, which can make
      // previously hidden items visible again. Reload instead of removing only
      // the selected rows locally so the grid and count reflect that change.
      await this.refreshMediaItemsPreservingPosition(ids);
      if (this.chronologyVisible) void this.reloadChronologyTree();
    } catch (error) {
      await ConfirmDialog.alert(error instanceof Error ? error.message : this.i18n.tr('gallery.selection.deleteFailed'), this.i18n.tr('common.error'));
    }
  }

  public settingVisibility = false;

  public async setSelectedVisibility(visibility: MediaVisibility): Promise<void> {
    if (!this.hasSelection || this.settingVisibility) return;

    const count = this.selectionCount;
    const message = count === 1
      ? this.i18n.tr('gallery.selection.setVisibilityConfirmOne', { visibility: this.i18n.tr(`media.visibility.${visibility}`) })
      : this.i18n.tr('gallery.selection.setVisibilityConfirmMany', { count, visibility: this.i18n.tr(`media.visibility.${visibility}`) });
    const confirmed = await ConfirmDialog.confirm(message, this.i18n.tr('gallery.selection.setVisibilityTitle'));
    if (!confirmed) return;

    this.settingVisibility = true;
    try {
      const ids = Object.keys(this.selectedIds).filter(id => this.selectedIds[id]);
      const updated = new Map<string, MediaItem>();
      for (const id of ids) {
        const item = this.mediaItems.find(m => m.id === id);
        if (!item) continue;
        updated.set(id, await updateMediaItem(id, {
          title: item.title,
          description: item.description,
          capturedAt: item.capturedAt,
          tagNames: item.tags.map(tag => tag.name),
          isCapturedAtTimeAccurate: item.isCapturedAtTimeAccurate,
          visibility,
        }));
      }
      this.mediaItems = this.mediaItems.map(item => updated.get(item.id) ?? item);
      this.clearSelection();
      this.selectMode = false;
    } catch (error) {
      await ConfirmDialog.alert(
        error instanceof Error ? error.message : this.i18n.tr('gallery.selection.setVisibilityFailed'),
        this.i18n.tr('common.error'),
      );
    } finally {
      this.settingVisibility = false;
    }
  }

  // ── Add-to-collection (via collection-picker) ───────────────────────────
  public showCollectionPicker = false;

  public openCollectionPicker(): void {
    if (!this.hasSelection) return;
    this.showCollectionPicker = true;
  }

  public onCollectionPickerClosed(): void {
    this.showCollectionPicker = false;
  }

  public async onCollectionPickerSelected(collection: CollectionRef): Promise<void> {
    this.showCollectionPicker = false;
    const ids = Object.keys(this.selectedIds).filter(id => this.selectedIds[id]);
    if (ids.length === 0) return;
    try {
      await addMediaItemsToCollection(collection.id, ids);
      await this.refreshAfterCollectionChange(ids);
    } catch (error) {
      await ConfirmDialog.alert(error instanceof Error ? error.message : String(error), this.i18n.tr('common.error'));
    }
  }

  public async onLightboxCollectionItemsAdded(added: number): Promise<void> {
    if (added > 0) await this.refreshAfterCollectionChange();
  }

  private async refreshAfterCollectionChange(changedIds?: Iterable<string>): Promise<void> {
    if (this.collectionFilter === CollectionFilter.Any) return;
    await this.refreshMediaItemsPreservingPosition(changedIds);
    if (this.chronologyVisible) await this.reloadChronologyTree();
  }

  public closeLightbox(lastItem?: MediaItem | null): void {
    this.lightboxItems = [];
    if (lastItem) {
      const index = this.mediaItems.findIndex(item => item.id === lastItem.id);
      if (index >= 0) void this.focusTile(index);
    }
  }

  public onLightboxSaved(item: MediaItem): void {
    this.mediaItems = this.mediaItems.map(existing => existing.id === item.id ? item : existing);
    this.lightboxItems = this.lightboxItems.map(existing => existing.id === item.id ? item : existing);
  }

  public async onLightboxFoldChanged(
    group: MediaFoldGroup | null,
    updatedItem: MediaItem | null,
    operation: FoldChangeOperation,
  ): Promise<void> {
    this.updateLightboxAfterFoldChange(group, updatedItem, operation);
    const changedIds = group?.items.map(item => item.id)
      ?? (updatedItem ? [updatedItem.id] : undefined);
    await this.refreshMediaItemsPreservingPosition(changedIds);
    if (this.chronologyVisible) void this.reloadChronologyTree();
  }

  public onLightboxDeleted(id: string): void {
    this.closeLightbox();
    void this.refreshMediaItemsPreservingPosition([id]);
    if (this.chronologyVisible) void this.reloadChronologyTree();
  }

  // Person marker data (created/reassigned/deleted) or the "no persons to mark"/"needs
  // revision" flags changed while the lightbox was open. Only affects the gallery grid/tree
  // when a person-review filter is active — otherwise membership/counts are unaffected — so
  // keep everything in sync by refetching in that case.
  public onLightboxPersonDataChanged(): void {
    if (this.personReviewFilter === PersonReviewFilter.None) return;
    void this.refreshMediaItemsPreservingPosition();
    if (this.chronologyVisible) void this.reloadChronologyTree();
  }

  private updateLightboxAfterFoldChange(
    group: MediaFoldGroup | null,
    updatedItem: MediaItem | null,
    operation: FoldChangeOperation,
  ): void {
    let items = [...this.lightboxItems];
    if (group) {
      const groupIds = new Set(group.items.map(item => item.id));
      const firstGroupIndex = items.findIndex(item => groupIds.has(item.id));
      items = items.filter(item => !groupIds.has(item.id));
      const replacementItems = operation === 'group'
        ? group.items.map(item => item.id === updatedItem?.id
          ? updatedItem
          : { ...item, foldedIntoMediaItemId: null, foldedImageCount: 0 })
        : operation === 'item'
          ? [
            { ...group.representative, foldedImageCount: Math.max(0, group.representative.foldedImageCount - 1) },
            ...(updatedItem ? [updatedItem] : []),
          ]
          : [group.representative];
      items.splice(firstGroupIndex >= 0 ? firstGroupIndex : items.length, 0, ...replacementItems);
    } else if (updatedItem) {
      const updatedIndex = items.findIndex(item => item.id === updatedItem.id);
      if (updatedIndex >= 0) items[updatedIndex] = updatedItem;
      else items.push(updatedItem);
    }
    this.lightboxItems = items;
  }

  public onDragEnter(event: DragEvent): void {
    if (!event.dataTransfer?.types.includes('Files')) return;
    event.preventDefault();
    this.dragDepth++;
    this.dragOverlayVisible = true;
  }

  public onDragLeave(event: DragEvent): void {
    if (!event.dataTransfer?.types.includes('Files')) return;
    event.preventDefault();
    this.dragDepth = Math.max(0, this.dragDepth - 1);
    if (this.dragDepth === 0) this.dragOverlayVisible = false;
  }

  public onDragOver(event: DragEvent): void {
    if (!event.dataTransfer?.types.includes('Files')) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
  }

  public async onDrop(event: DragEvent): Promise<void> {
    event.preventDefault();
    this.dragDepth = 0;
    this.dragOverlayVisible = false;
    const files = event.dataTransfer?.files ? Array.from(event.dataTransfer.files) : [];
    if (files.length === 0) return;

    const batchItems: UploadProgressItem[] = files.map(file => ({
      id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      fileName: file.name,
      size: file.size,
      uploadedBytes: 0,
      status: 'queued',
      message: '',
    }));
    this.uploadItems = batchItems;
    this.uploadStartedAt = Date.now();
    this.uploadModalVisible = true;

    let protocolNumber: number | undefined;
    try {
      const protocol = await createImportProtocol();
      protocolNumber = protocol.number;
    } catch (error) {
      console.error('Failed to create import protocol for upload batch.', error);
    }

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const progressItem = batchItems[i];

      try {
        progressItem.status = 'uploading';
        this.uploadItems = [...this.uploadItems];
        const uploaded = await uploadMediaFile(file, protocolNumber, (_percent, loaded) => {
          progressItem.uploadedBytes = loaded;
          this.uploadItems = [...this.uploadItems];
        }, getDefaultVisibility());
        progressItem.status = uploaded.isDuplicate ? 'duplicate' : 'done';
        progressItem.uploadedBytes = progressItem.size;
        progressItem.message = uploaded.isDuplicate
          ? this.i18n.tr('gallery.upload.duplicateItem')
          : uploaded.title;
        this.uploadItems = [...this.uploadItems];
      } catch (error) {
        progressItem.status = 'error';
        progressItem.message = error instanceof Error ? error.message : String(error);
        this.uploadItems = [...this.uploadItems];
      }
    }

    if (protocolNumber != null) {
      try {
        await completeImportProtocol(protocolNumber);
      } catch (error) {
        console.error(`Failed to complete import protocol ${protocolNumber}.`, error);
      }
    }

    await this.refreshMediaItemsPreservingPosition();
    if (this.chronologyVisible) void this.reloadChronologyTree();
  }

  public dismissUploadModal(): void {
    this.uploadModalVisible = false;
    this.uploadItems = [];
    this.uploadStartedAt = null;
  }

  // ── Chronology tree ────────────────────────────────────────────────────────

  public async toggleChronology(): Promise<void> {
    if (this.dateFilterActive || this.isSelectingAll) return;
    this.showChronology = !this.showChronology;
    localStorage.setItem(CHRONOLOGY_STORAGE_KEY, String(this.showChronology));
    if (this.showChronology && !this.chronologyTree) {
      await this.reloadChronologyTree();
    }
  }

  public async reloadChronologyTree(): Promise<void> {
    if (!this.chronologyVisible) {
      if (this.dateFilterActive) this.chronologyTree = null;
      return;
    }
    try {
      const chronologyTree = await getChronologyTree(
        this.searchText.trim() || undefined,
        this.includeDescription,
        this.filterTags.filter(tag => tag.id).map(tag => tag.id),
        this.matchAllTags,
        this.mediaTypeFilter,
        this.parsedProtocolNumber,
        this.personReviewFilter,
        this.personFilterMode === 'advanced' ? [] : this.filterPersons.map(p => p.id),
        this.personFilterMode === 'advanced' ? false : this.matchAllPersons,
        this.collectionFilter === CollectionFilter.NotInCollection,
        this.collectionFilter === CollectionFilter.InCollection,
        getShowFamilyMedia(),
        this.advancedPersonFilter ?? undefined,
        this.onlyFolded,
        this.embeddedMetadataFilter ?? undefined,
        this.captureTimeAccuracyFilter ?? undefined,
      );
      if (this.chronologyVisible) this.chronologyTree = chronologyTree;
    } catch { /* ignore */ }
  }

  public toggleYear(year: number): void {
    if (this.dateFilterActive) return;
    this.expandedYears = { ...this.expandedYears, [year]: !this.expandedYears[year] };
  }

  public toggleMonth(year: number, month: number): void {
    if (this.dateFilterActive) return;
    const key = `${year}-${month}`;
    this.expandedMonths = { ...this.expandedMonths, [key]: !this.expandedMonths[key] };
  }

  public selectAll(): void {
    if (this.isSelectingAll || this.dateFilterActive) return;
    this.selType = 'all';
    void this.fetchMediaItems();
  }

  public selectUnknown(): void {
    if (this.isSelectingAll || this.dateFilterActive) return;
    this.selType = 'unknown';
    void this.fetchMediaItems();
  }

  public selectYear(year: number): void {
    if (this.isSelectingAll || this.dateFilterActive) return;
    this.selType = 'year';
    this.selYear = year;
    void this.fetchMediaItems();
  }

  public selectMonth(year: number, month: number): void {
    if (this.isSelectingAll || this.dateFilterActive) return;
    this.selType = 'month';
    this.selYear = year;
    this.selMonth = month;
    void this.fetchMediaItems();
  }

  public onYearClick(year: number): void {
    if (this.isSelectingAll || this.dateFilterActive) return;
    this.toggleYear(year);
    this.selType = 'year';
    this.selYear = year;
    void this.fetchMediaItems();
  }

  public onMonthClick(year: number, month: number): void {
    if (this.isSelectingAll || this.dateFilterActive) return;
    this.toggleMonth(year, month);
    this.selType = 'month';
    this.selYear = year;
    this.selMonth = month;
    void this.fetchMediaItems();
  }

  public selectDay(year: number, month: number, day: number): void {
    if (this.isSelectingAll || this.dateFilterActive) return;
    this.selType = 'day';
    this.selYear = year;
    this.selMonth = month;
    this.selDay = day;
    void this.fetchMediaItems();
  }

  public get breadcrumbItems(): Array<{ label: string; type: BreadcrumbType; isLast: boolean }> {
    const locale = this._locale; // tracked — re-evaluates this getter when locale changes
    if (this.dateFilterActive) {
      return [{
        label: this.dateFilterBreadcrumbLabel(),
        type: 'dateFilter',
        isLast: true,
      }];
    }

    const allLabel = this.i18n.tr('gallery.chronology.all');
    const items: Array<{ label: string; type: BreadcrumbType; isLast: boolean }> = [
      { label: allLabel, type: 'all', isLast: this.selType === 'all' },
    ];
    if (this.selType === 'unknown') {
      items.push({ label: this.i18n.tr('gallery.chronology.unknown'), type: 'unknown', isLast: true });
      return items;
    }
    if (this.selType === 'year' || this.selType === 'month' || this.selType === 'day') {
      items.push({ label: String(this.selYear), type: 'year', isLast: this.selType === 'year' });
    }
    if (this.selType === 'month' || this.selType === 'day') {
      items.push({ label: this.monthName(this.selMonth), type: 'month', isLast: this.selType === 'month' });
    }
    if (this.selType === 'day') {
      items.push({ label: String(this.selDay) + '.', type: 'day', isLast: true });
    }
    return items;
  }

  public onBreadcrumbClick(type: BreadcrumbType): void {
    if (type === 'dateFilter') return;
    if (type === 'all') this.selectAll();
    else if (type === 'year') this.selectYear(this.selYear);
    else if (type === 'month') this.selectMonth(this.selYear, this.selMonth);
    // day/unknown = current, no navigation needed
  }

  public monthName(month: number): string {
    return new Date(2000, month - 1, 1).toLocaleString(this._locale || 'en', { month: 'long' });
  }

  private dateFilterBreadcrumbLabel(): string {
    const from = this.dateFrom ? this.formatFilterDate(this.dateFrom) : '';
    const to = this.dateTo ? this.formatFilterDate(this.dateTo) : '';
    let range: string;
    if (from && to) {
      range = this.i18n.tr('gallery.chronology.dateFilterRange', { from, to });
    } else if (from) {
      range = this.i18n.tr('gallery.chronology.dateFilterFrom', { date: from });
    } else {
      range = this.i18n.tr('gallery.chronology.dateFilterTo', { date: to });
    }
    return `${this.i18n.tr('gallery.chronology.dateFilter')}: ${range}`;
  }

  private formatFilterDate(value: string): string {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!match) return value;
    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return new Intl.DateTimeFormat(this._locale || undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  }

  private effectiveDateRange(): { dateFrom?: string; dateTo?: string; onlyUnknownCapturedAt?: boolean } {
    if (this.dateFilterActive) {
      return {
        dateFrom: this.dateFrom || undefined,
        // The repository treats dateTo as an exclusive boundary while the date
        // input represents an inclusive calendar day.
        dateTo: this.dateTo ? this.nextDate(this.dateTo) : undefined,
      };
    }
    if (this.selType === 'unknown') return { onlyUnknownCapturedAt: true };
    const treeRange = this.selectionDateRange();
    if (treeRange.dateFrom || treeRange.dateTo) return treeRange;
    return { dateFrom: this.dateFrom || undefined, dateTo: this.dateTo || undefined };
  }

  private nextDate(value: string): string {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!match) return value;
    const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]) + 1));
    return date.toISOString().slice(0, 10);
  }

  private resetChronologySelection(): void {
    this.selType = 'all';
    this.selYear = 0;
    this.selMonth = 0;
    this.selDay = 0;
    this.chronologyTree = null;
  }

  private updateChronologyForDateFilter(): void {
    if (this.dateFilterActive) {
      if (!this._chronologySelectionBeforeDateFilter) {
        this._chronologySelectionBeforeDateFilter = {
          selType: this.selType,
          selYear: this.selYear,
          selMonth: this.selMonth,
          selDay: this.selDay,
        };
      }
      this.resetChronologySelection();
      return;
    }

    if (this._chronologySelectionBeforeDateFilter) {
      this.selType = this._chronologySelectionBeforeDateFilter.selType;
      this.selYear = this._chronologySelectionBeforeDateFilter.selYear;
      this.selMonth = this._chronologySelectionBeforeDateFilter.selMonth;
      this.selDay = this._chronologySelectionBeforeDateFilter.selDay;
      this._chronologySelectionBeforeDateFilter = null;
      this.chronologyTree = null;
    }
  }

  private selectionDateRange(): { dateFrom?: string; dateTo?: string } {
    if (this.selType === 'all') return {};
    if (this.selType === 'year') {
      return { dateFrom: `${this.selYear}-01-01`, dateTo: `${this.selYear + 1}-01-01` };
    }
    if (this.selType === 'month') {
      const nextMonth = this.selMonth === 12
        ? `${this.selYear + 1}-01`
        : `${this.selYear}-${String(this.selMonth + 1).padStart(2, '0')}`;
      return {
        dateFrom: `${this.selYear}-${String(this.selMonth).padStart(2, '0')}-01`,
        dateTo:   `${nextMonth}-01`,
      };
    }
    if (this.selType === 'day') {
      const pad = (n: number) => String(n).padStart(2, '0');
      const next = new Date(this.selYear, this.selMonth - 1, this.selDay + 1);
      return {
        dateFrom: `${this.selYear}-${pad(this.selMonth)}-${pad(this.selDay)}`,
        dateTo:   `${next.getFullYear()}-${pad(next.getMonth() + 1)}-${pad(next.getDate())}`,
      };
    }
    return {};
  }
}
